import { createToken, CstParser, Lexer, tokenMatcher } from 'chevrotain';
import mustache from 'mustache';
import bigDecimal from 'js-big-decimal';
import { IGizmoCallbacks } from '../types';
import { Gizmo } from '../gizmos';
import { Dao } from '../dao';
import functions from './functions';
import influx from './influx';
import { snov } from '../utils';

const Influx = createToken({
  name: 'Influx',
  pattern: /{{\s*[\w\.]+\s*}}/,
});
const Constant = createToken({
  name: 'Constant',
  // pattern: /EMPTY\|NULL/
  pattern: /EMPTY|NULL/,
});
const Identifier = createToken({
  name: 'Identifier',
  pattern: /[a-zA-Z]\w*/,
});

const Comma = createToken({ name: 'Comma', pattern: /,/ });
const Integer = createToken({ name: 'Integer', pattern: /0|[1-9]\d*/ });
const String = createToken({ name: 'String', pattern: /".*?"/ });

const Equals = createToken({ name: 'Equals', pattern: /=/ });

const ComparisonOperator = createToken({
  name: 'ComparisonOperator',
  pattern: Lexer.NA,
});
const EqualTo = createToken({
  name: 'EqualTo',
  pattern: /==/,
  categories: ComparisonOperator,
});
const NotEqualTo = createToken({
  name: 'NotEqualTo',
  pattern: /!=/,
  categories: ComparisonOperator,
});
const GreaterThan = createToken({
  name: 'GreaterThan',
  pattern: />/,
  categories: ComparisonOperator,
});
const GreaterThanOrEqual = createToken({
  name: 'GreaterThanOrEqual',
  pattern: />=/,
  categories: ComparisonOperator,
});
const LessThan = createToken({
  name: 'LessThan',
  pattern: /</,
  categories: ComparisonOperator,
});
const LessThanOrEqual = createToken({
  name: 'LessThaOrEqual',
  pattern: /<=/,
  categories: ComparisonOperator,
});

const AdditionOperator = createToken({
  name: 'AdditionOperator',
  pattern: Lexer.NA,
});
const Plus = createToken({
  name: 'Plus',
  pattern: /\+/,
  categories: AdditionOperator,
});
const Minus = createToken({
  name: 'Minus',
  pattern: /-/,
  categories: AdditionOperator,
});

const MultiplicationOperator = createToken({
  name: 'MultiplicationOperator',
  pattern: Lexer.NA,
});
const Multi = createToken({
  name: 'Multi',
  pattern: /\*/,
  categories: MultiplicationOperator,
});
const Div = createToken({
  name: 'Div',
  pattern: /\//,
  categories: MultiplicationOperator,
});

const LogicalOperator = createToken({
  name: 'LogicalOperator',
  pattern: Lexer.NA,
});
const And = createToken({
  name: 'And',
  pattern: /AND|&&/,
  categories: LogicalOperator,
});
const Or = createToken({
  name: 'Or',
  pattern: /OR|\|\|/,
  categories: LogicalOperator,
});

const LParen = createToken({ name: 'LParen', pattern: /\(/ });
const RParen = createToken({ name: 'RParen', pattern: /\)/ });

const WhiteSpace = createToken({
  name: 'WhiteSpace',
  pattern: /\s+/,
  group: Lexer.SKIPPED,
});

// note we are placing WhiteSpace first as it is very common thus it will speed up the lexer.
let allTokens = [
  WhiteSpace,
  Influx,
  Constant,

  Integer,
  String,

  Comma,

  // Comparison operators
  EqualTo,
  NotEqualTo,
  Equals,
  GreaterThanOrEqual,
  GreaterThan,
  LessThanOrEqual,
  LessThan,

  // Logical operators
  LogicalOperator,
  And,
  Or,

  // Addition
  AdditionOperator,
  Plus,
  Minus,

  // Multiplication operators
  MultiplicationOperator,
  Multi,
  Div,

  LParen,
  RParen,

  //! We commented the Identifier because for some reason the AND and OR operators were
  //! being identified as... Identifiers... no pun intended
  // The Identifier must appear after the keywords because all keywords are valid identifiers.
  Identifier,
];

const FluxLexer = new Lexer(allTokens);

//--------------- parser ------------------
class FluxParser extends CstParser {
  constructor() {
    super(allTokens);
    this.performSelfAnalysis();
  }

  //----- Setter functions ---------//
  // public setDax = this.RULE('setDax', () => {});
  // public setSynchronizer = this.RULE('setSynchronizer', () => {});
  public setDao = this.RULE('setDao', () => {});
  public setGizmoCallbacks = this.RULE('setGizmoCallbacks', () => {});
  public setGizmo = this.RULE('setGizmo', () => {});
  //----- Setter functions end ---------//

  public program = this.RULE('program', () => {
    this.MANY(() => {
      this.SUBRULE(this.statement);
      // this.SUBRULE(this.func);
    });
  });

  // I'm not sure if we need a rule for the call and one for the function declaration
  public functionCall = this.RULE('functionCall', () => {
    this.CONSUME(Identifier);
    this.CONSUME(LParen);
    this.OPTION(() => {
      this.SUBRULE(this.funcArgs);
      this.MANY(() => {
        this.CONSUME(Comma);
        this.SUBRULE2(this.funcArgs);
      });
    });
    this.CONSUME(RParen);
  });

  public funcArgs = this.RULE('funcArgs', () => {
    this.OR([
      { ALT: () => this.SUBRULE(this.functionCall) },

      { ALT: () => this.CONSUME2(Identifier) },
      { ALT: () => this.CONSUME(String) },
      { ALT: () => this.CONSUME(Integer) },
      { ALT: () => this.CONSUME(Influx) },
    ]);
  });

  public statement = this.RULE('statement', () => {
    this.OR([{ ALT: () => this.SUBRULE(this.expressionStatement) }]);
  });

  public expressionStatement = this.RULE('expressionStatement', () => {
    this.SUBRULE(this.expression);
  });

  public expression = this.RULE('expression', () => {
    this.OR([
      // { ALT: () => this.SUBRULE(this.assignExpression) },
      { ALT: () => this.SUBRULE(this.relationExpression) },
    ]);
  });

  public relationExpression = this.RULE('relationExpression', () => {
    // this.SUBRULE(this.additionExpression, { LABEL: 'lhs' });
    // this.MANY(() => {
    //   this.CONSUME(ComparisonOperator);
    //   this.SUBRULE2(this.additionExpression, { LABEL: 'rhs' });
    // });

    this.SUBRULE(this.comparisonExpression, { LABEL: 'lhs' });
    this.MANY(() => {
      this.CONSUME(LogicalOperator);
      this.SUBRULE2(this.comparisonExpression, { LABEL: 'rhs' });
    });
  });

  public comparisonExpression = this.RULE('comparisonExpression', () => {
    this.SUBRULE(this.additionExpression, { LABEL: 'lhs' });
    this.MANY(() => {
      this.CONSUME(ComparisonOperator);
      this.SUBRULE2(this.additionExpression, { LABEL: 'rhs' });
    });
  });

  public additionExpression = this.RULE('additionExpression', () => {
    this.SUBRULE(this.multiplicationExpression, { LABEL: 'lhs' });
    this.MANY(() => {
      this.CONSUME(AdditionOperator);
      this.SUBRULE2(this.multiplicationExpression, { LABEL: 'rhs' });
    });
  });

  public multiplicationExpression = this.RULE(
    'multiplicationExpression',
    () => {
      this.SUBRULE(this.term, { LABEL: 'lhs' });
      this.MANY(() => {
        this.CONSUME(MultiplicationOperator);
        this.SUBRULE2(this.term, { LABEL: 'rhs' });
      });
    }
  );

  public assignExpression = this.RULE('assignExpression', () => {
    this.CONSUME(Identifier);
    this.CONSUME(Equals);
    this.SUBRULE(this.expression);
  });

  public term = this.RULE('term', () => {
    this.OR([
      { ALT: () => this.SUBRULE(this.functionCall) },

      { ALT: () => this.CONSUME(Constant) },

      { ALT: () => this.CONSUME(Identifier) },
      { ALT: () => this.CONSUME(Integer) },

      { ALT: () => this.SUBRULE(this.parent_expr) },

      { ALT: () => this.CONSUME(Influx) },
      { ALT: () => this.CONSUME(String) },
    ]);
  });

  public parent_expr = this.RULE('parent_expr', () => {
    this.CONSUME(LParen);
    this.SUBRULE(this.expression);
    this.CONSUME(RParen);
  });
}

const parser = new FluxParser();

// ------------------ Interpreter --------------------
// Obtains the default CstVisitor constructor to extend
const BaseCstVisitor = parser.getBaseCstVisitorConstructor();
// All aour semantics go into the visitor, completely separated from the grammar.
class FluxInterpreter extends BaseCstVisitor {
  private _dao?: Dao;
  private _gizmoCallbacks?: IGizmoCallbacks;
  private _gizmo?: Gizmo;

  constructor() {
    super();

    // This helper will detect any missing or redundant methods on this visitor
    this.validateVisitor();
  }

  public setDao(dao?: Dao) {
    this._dao = dao;
  }

  public setGizmoCallbacks(gizmoCallbacks: IGizmoCallbacks) {
    this._gizmoCallbacks = gizmoCallbacks;
  }

  public setGizmo(gizmo?: Gizmo) {
    this._gizmo = gizmo;
  }

  public program(ctx: any): Promise<any> {
    return new Promise<any>(async resolve => {
      const result = await this.visit(ctx.statement);
      resolve(result);
    });
  }

  public functionCall(ctx: any): Promise<any> {
    return new Promise<any>(async resolve => {
      const args: any[] = [];

      if (ctx.funcArgs) {
        for (const funcArgs of ctx.funcArgs) {
          const arg = await this.visit(funcArgs);

          args.push(arg);
        }
      }

      if (ctx.Identifier && ctx.Identifier[0].image && this._gizmoCallbacks) {
        const result = await functions({
          functionName: ctx.Identifier[0].image,
          args,
          context: {
            gizmo: this._gizmo,
            // dax: this._dax,
            // gizmoCallbacks: this._gizmoCallbacks,
          },
        });

        return resolve(result);
      }

      resolve(undefined);
    });
  }

  public funcArgs(ctx: any): Promise<any> {
    return new Promise<any>(async resolve => {
      const result = await this.term(ctx);

      resolve(result);
    });
  }

  public statement(ctx: any): Promise<any> {
    return new Promise<any>(async resolve => {
      const result = await this.visit(ctx.expressionStatement);
      resolve(result);
    });
  }

  public expressionStatement(ctx: any): Promise<any> {
    return new Promise<any>(async resolve => {
      const result = await this.visit(ctx.expression);
      resolve(result);
    });
  }

  public expression(ctx: any): Promise<any> {
    return new Promise<any>(async resolve => {
      if (ctx.relationExpression) {
        const result = await this.visit(ctx.relationExpression);
        return resolve(result);
      }

      resolve(undefined);
    });
  }

  public relationExpression(ctx: any): Promise<any> {
    return new Promise<any>(async resolve => {
      let result = await this.visit(ctx.lhs);

      if (ctx.rhs) {
        let idx = 0;

        for (const rhsOperand of ctx.rhs) {
          let rhsValue = await this.visit(rhsOperand);
          let operator = ctx.LogicalOperator[idx];

          if (tokenMatcher(operator, And)) {
            result = result && rhsValue;
          } else {
            result = result || rhsValue;
          }

          idx++;
        }
      }

      resolve(result);
    });
  }

  public comparisonExpression(ctx: any): Promise<any> {
    return new Promise<any>(async resolve => {
      let result = await this.visit(ctx.lhs);

      if (ctx.rhs) {
        let idx = 0;

        for (const rhsOperand of ctx.rhs) {
          let rhsValue = await this.visit(rhsOperand);
          let operator = ctx.ComparisonOperator[idx];

          if (tokenMatcher(operator, GreaterThan)) {
            result = result > rhsValue;
          } else if (tokenMatcher(operator, GreaterThanOrEqual)) {
            result = result >= rhsValue;
          } else if (tokenMatcher(operator, LessThan)) {
            result = result < rhsValue;
          } else if (tokenMatcher(operator, LessThanOrEqual)) {
            result = result <= rhsValue;
          } else if (tokenMatcher(operator, EqualTo)) {
            result = result === rhsValue;
          } else if (tokenMatcher(operator, NotEqualTo)) {
            result = result !== rhsValue;
          }

          idx++;
        }
      }

      resolve(result);
    });
  }

  public additionExpression(ctx: any): Promise<any> {
    return new Promise<any>(async resolve => {
      let result = await this.visit(ctx.lhs);

      if (ctx.rhs) {
        let idx = 0;

        for (const rhsOperand of ctx.rhs) {
          let rhsValue = await this.visit(rhsOperand);
          let operator = ctx.AdditionOperator[idx];

          if (tokenMatcher(operator, Plus)) {
            if (typeof result === 'number' && typeof rhsValue === 'number') {
              result = Number(
                bigDecimal.add(result.toString(), rhsValue.toString())
              );
            } else {
              result += rhsValue;
            }
          } else {
            if (typeof result === 'number' && typeof rhsValue === 'number') {
              result = Number(
                bigDecimal.subtract(result.toString(), rhsValue.toString())
              );
            } else {
              result -= rhsValue;
            }
          }
          idx++;
        }
      }

      resolve(result);
    });
  }

  public multiplicationExpression(ctx: any): Promise<any> {
    return new Promise<any>(async resolve => {
      let result = await this.visit(ctx.lhs);

      if (ctx.rhs) {
        let idx = 0;

        for (const rhsOperand of ctx.rhs) {
          let rhsValue = await this.visit(rhsOperand);
          let operator = ctx.MultiplicationOperator[idx];

          if (tokenMatcher(operator, Multi)) {
            if (typeof result === 'number' && typeof rhsValue === 'number') {
              result = Number(
                bigDecimal.multiply(result.toString(), rhsValue.toString())
              );
            }
          } else {
            if (typeof result === 'number' && typeof rhsValue === 'number') {
              // By default we're going to leave 2 decimals, should we leave more?
              result = Number(
                bigDecimal.divide(result.toString(), rhsValue.toString(), 2)
              );
            }
          }

          idx++;
        }
      }

      resolve(result);
    });
  }

  public assignExpression(ctx: any) {
    console.log('assignExpression: ', ctx);
  }

  public term(ctx: any): Promise<any> {
    return new Promise<any>(async resolve => {
      if (ctx.Integer) {
        return resolve(parseInt(ctx.Integer[0].image, 10));
      } else if (ctx.String) {
        return resolve(ctx.String[0].image.replace(/"/g, ''));
      } else if (ctx.functionCall) {
        const functionResult = await this.visit(ctx.functionCall);

        if (functionResult !== undefined) {
          return resolve(functionResult);
        }
      } else if (ctx.Influx) {
        if (this._gizmoCallbacks) {
          // const influxResult = await influx({
          //   variable: ctx.Influx[0].image
          //     .replace('{{', '')
          //     .replace('}}', '')
          //     .trim(),
          //   context: {
          //     dax: this._dax,
          //     gizmoCallbacks: this._gizmoCallbacks,
          //     gizmo: this._gizmo,
          //   },
          // });

          const influxResult = await influx({
            variable: ctx.Influx[0].image
              .replace('{{', '')
              .replace('}}', '')
              .trim(),
            context: {
              gizmoCallbacks: this._gizmoCallbacks,
              // synchronizer: this._synchronizer,
              dao: this._dao,
              gizmo: this._gizmo,
            },
          });

          return resolve(influxResult);
        }
      } else if (ctx.parent_expr) {
        const parenthesesResult = await this.visit(ctx.parent_expr);
        return resolve(parenthesesResult);
      } else if (ctx.Constant) {
        if (ctx.Constant[0].image === 'EMPTY') {
          return resolve(undefined);
        }
      }

      resolve(undefined);
    });
  }

  public parent_expr(ctx: any): Promise<any> {
    return new Promise<any>(async resolve => {
      const result = await this.visit(ctx.expression);
      resolve(result);
    });
  }
}

interface IFluxContructor {
  gizmoCallbacks: IGizmoCallbacks;
  // synchronizer?: Synchronizer;
  dao?: Dao;
  /**
   * Gizmo that's creating the Flux instance
   */
  gizmo?: Gizmo;
}

interface IEvaluateParams {
  script: string;
}

class Flux {
  private _gizmoCallbacks: IGizmoCallbacks;
  // private _synchronizer?: Synchronizer;
  private _dao?: Dao;
  private _gizmo?: Gizmo;
  private _interpreter: FluxInterpreter;

  constructor({ gizmoCallbacks, dao, gizmo }: IFluxContructor) {
    this._gizmoCallbacks = gizmoCallbacks;
    this._dao = dao;
    this._gizmo = gizmo;

    this._interpreter = new FluxInterpreter();
  }

  public init = (): Promise<void> => {
    return new Promise<void>(async resolve => {
      resolve();
    });
  };

  public evaluate = ({ script }: IEvaluateParams): Promise<any> => {
    return new Promise<any>(async resolve => {
      const lexResult = FluxLexer.tokenize(script);

      // setting a new input will RESET the parser instance's state.
      parser.input = lexResult.tokens;

      // any top level rule may be used as an entry point
      const cst = parser.program();

      // this._interpreter.setSynchronizer(this._synchronizer);
      this._interpreter.setDao(this._dao);
      this._interpreter.setGizmoCallbacks(this._gizmoCallbacks);
      this._interpreter.setGizmo(this._gizmo);

      // Note that separation of concerns between the syntatic analysis (parsing)
      // and the semantics.
      const value = await this._interpreter.visit(cst);

      resolve(value);
    });
  };

  public interpolate = ({ script }: IEvaluateParams): Promise<string> => {
    return new Promise<string>(async resolve => {
      const serverVariablesMatches = script.match(/\${{\s*[\w\.]+\s*}}/g);
      const variableMatches = script.match(/{{\s*[\w\.]+\s*}}/g);
      const mustacheData: any = {};

      if (variableMatches) {
        for (const match of variableMatches) {
          const fluxVar = match
            .replace(/ /g, '')
            .replace('{{', '')
            .replace('}}', '')
            .trim();

          let value = await influx({
            variable: fluxVar,
            context: {
              gizmoCallbacks: this._gizmoCallbacks,
              // synchronizer: this._synchronizer,
              dao: this._dao,
              gizmo: this._gizmo,
            },
          });

          if (serverVariablesMatches && typeof value === 'string') {
            // If we have serverVariable matches it means the interpolation will be send to the
            // server
            value = `"${value}"`;
          }

          snov(mustacheData, fluxVar, value);
        }
      }
      if (serverVariablesMatches) {
        for (const match of serverVariablesMatches) {
          const value = match.replace('$', '');
          const serverFluxVar = match
            .replace('{{', '')
            .replace('}}', '')
            .replace(/ /g, '')
            .trim();

          snov(mustacheData, serverFluxVar, value);
        }
      }

      const result = mustache.render(
        script
          .replace(/\${{/g, '{{$')
          .replace(/{{/g, '{{{')
          .replace(/}}/g, '}}}'),
        mustacheData
      );

      resolve(result);
    });
  };
}

export default Flux;
